/**
 * CodeEditor - Reusable code editor component wrapping CodeMirror
 * 
 * Features:
 * - Syntax highlighting for JavaScript/JSX
 * - Line numbers
 * - Dark mode support (auto-detects)
 * - Read-only mode
 * - Customizable height
 */

'use client';

import * as React from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { githubDark, githubLight } from '@uiw/codemirror-theme-github';
import { cn } from '@/lib/utils';

/**
 * Hook to detect dark mode (prefers-color-scheme or .dark class on html)
 */
export function useDarkMode() {
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(mediaQuery.matches || document.documentElement.classList.contains('dark'));

    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);

    // Also observe class changes on html element (for manual theme toggle)
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      mediaQuery.removeEventListener('change', handler);
      observer.disconnect();
    };
  }, []);

  return isDark;
}

export interface CodeEditorProps {
  /** Current value */
  value: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Editor height (default: '150px') */
  height?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Read-only mode */
  readOnly?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Show line numbers (default: true) */
  lineNumbers?: boolean;
  /** Highlight active line (default: true) */
  highlightActiveLine?: boolean;
  /** Language mode (default: 'javascript') */
  language?: 'javascript' | 'json' | 'html' | 'css';
  /** Additional class name */
  className?: string;
  /** Force dark/light theme (default: auto-detect) */
  theme?: 'dark' | 'light' | 'auto';
}

/**
 * CodeEditor component - A professional code editor with syntax highlighting
 */
export function CodeEditor({
  value,
  onChange,
  height = '150px',
  placeholder,
  readOnly = false,
  disabled = false,
  lineNumbers = true,
  highlightActiveLine = true,
  language = 'javascript',
  className,
  theme = 'auto',
}: CodeEditorProps) {
  const systemDark = useDarkMode();
  const isDark = theme === 'auto' ? systemDark : theme === 'dark';

  // Language extensions
  const extensions = React.useMemo(() => {
    switch (language) {
      case 'javascript':
        return [javascript({ jsx: true, typescript: false })];
      case 'json':
        return [javascript()]; // JSON uses JS parser
      default:
        return [javascript({ jsx: true })];
    }
  }, [language]);

  return (
    <div className={cn('overflow-hidden rounded-md border dark:border-slate-700', className)}>
      <CodeMirror
        value={value}
        onChange={onChange}
        height={height}
        extensions={extensions}
        theme={isDark ? githubDark : githubLight}
        editable={!readOnly && !disabled}
        placeholder={placeholder}
        basicSetup={{
          lineNumbers,
          foldGutter: false,
          highlightActiveLine: highlightActiveLine && !readOnly,
          highlightSelectionMatches: !readOnly,
          autocompletion: false,
          bracketMatching: true,
          closeBrackets: true,
          indentOnInput: true,
        }}
        className="text-xs"
      />
    </div>
  );
}

/**
 * CodeViewer - Read-only code display with syntax highlighting
 */
export function CodeViewer({
  value,
  height = '200px',
  language = 'javascript',
  className,
  theme = 'dark',
}: Omit<CodeEditorProps, 'onChange' | 'readOnly' | 'disabled' | 'placeholder'>) {
  return (
    <CodeEditor
      value={value}
      height={height}
      language={language}
      className={className}
      theme={theme}
      readOnly
      highlightActiveLine={false}
      lineNumbers
    />
  );
}

export default CodeEditor;

